import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ErrorMessage } from 'formik'
import styles from '../../UpdateSiteModal.module.css'
import {
  DISPLAY_MODE_STANDARD,
  DISPLAY_MODE_PRESENTATION,
  DISPLAY_MODE_MINIMALIST,
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_EMAIL_WHITELIST,
  GATED_MODE_STANDARD,
  GATED_MODE_SSO,
  PDF_FILE,
  ZIP_FILE
} from '../../constants'
import { addUpgradeReason, hasFeature, isWasmerFile, onUpgradePlan } from 'utils/general'
import { setShowPaymentModal, UPGRADE_MINIMALIST_PAYLOAD } from 'actions'
import { PLANS } from 'constants/plans'
import { MAX_DIRECT_UPLOAD_SIZE } from 'services/create-update-site/pro'
import {
  F_EMAIL_GATE,
  F_PWD_PROTECTION,
  F_MINIMALIST_MODE,
  F_PRESENTATION_MODE,
  F_EMAIL_WHITELIST
} from 'constants/plans/constants'
import { AccessOption, Toggle, AccessModeDropdown } from './AccessToggle'
import { UPGRADE_PRESENTATION_PAYLOAD, UPGRADE_CAPTURE_EMAILS_PAYLOAD, UPGRADE_PASSWORD_PROTECT_PAYLOAD } from 'actions'
import { showUpgradeCardModal } from 'actions'
import EmailTagInput from 'components/CreateUpdateSiteModal/form/components/foundations/EmailTagInput'
import useEnterpriseSso from 'hooks/useEnterpriseSso'

const AccessSettings = ({ uploadValues, toggleValues, setFieldValue, name, productId, sectionIcon, site }) => {
  const dispatch = useDispatch()
  const userProfile = useSelector((state) => state.manage.userProfile) || {}
  const isChildAccount = !!userProfile.childAccount
  const { needsSetup, selectSsoMode } = useEnterpriseSso({
    enabled: !isChildAccount
  })

  const showSsoOption = !isChildAccount || toggleValues.gatedMode === GATED_MODE_SSO

  // Access mode options
  const accessModeOptions = [
    {
      value: GATED_MODE_STANDARD,
      label: 'Public (No restrictions)',
      description: 'Anyone with the link can view'
    },
    {
      value: GATED_MODE_PASSWORD,
      label: 'Password Protect',
      description: 'Make your link private by adding a layer of security'
    },
    {
      value: GATED_MODE_EMAIL_GATE,
      label: 'Capture Emails',
      description: 'Capture potential leads by requiring visitors to enter their email address'
    },
    {
      value: GATED_MODE_EMAIL_WHITELIST,
      label: 'Email protect',
      description: 'Limit access to approved users only'
    },
    ...(showSsoOption
      ? [
          {
            value: GATED_MODE_SSO,
            label: 'Require SSO login',
            description: needsSetup
              ? 'Finish SSO setup first'
              : 'Restrict access to users authenticated with company SSO',
            onSelect: () => (isChildAccount ? false : selectSsoMode())
          }
        ]
      : [])
  ]

  const displayModeOptions = [
    {
      value: DISPLAY_MODE_STANDARD,
      label: 'Standard Mode',
      description: 'Standard display for your PDF files'
    },
    {
      value: DISPLAY_MODE_PRESENTATION,
      label: 'Presentation Mode',
      description: 'Displays PDF as a slideshow'
    },
    {
      value: DISPLAY_MODE_MINIMALIST,
      label: 'Minimalist PDF Mode',
      description: 'Displays only the PDF without any other elements'
    }
  ]

  // Check if user has access to the selected feature
  const checkAccessAndDisplayModeFeature = (value) => {
    if (value === GATED_MODE_PASSWORD && !hasFeature(productId, F_PWD_PROTECTION)) {
      onUpgrade(PLANS.PRO.id, UPGRADE_PASSWORD_PROTECT_PAYLOAD)
      return false
    }

    if (value === GATED_MODE_EMAIL_GATE && !hasFeature(productId, F_EMAIL_GATE)) {
      onUpgrade(PLANS.PRO_U.id, UPGRADE_CAPTURE_EMAILS_PAYLOAD)
      return false
    }

    if (value === GATED_MODE_EMAIL_WHITELIST && !hasFeature(productId, F_EMAIL_WHITELIST)) {
      onUpgrade(PLANS.PRO_U.id, {
        title: 'Upgrade for Email Access Protection',
        desc: 'Limit access to your project to approved users only',
        cta: 'Upgrade now',
        gif: null
      })
      return false
    }

    if (value === DISPLAY_MODE_PRESENTATION && !hasFeature(productId, F_PRESENTATION_MODE)) {
      onUpgrade(PLANS.TINY.id, UPGRADE_PRESENTATION_PAYLOAD)
      return false
    }

    if (value === DISPLAY_MODE_MINIMALIST && !hasFeature(productId, F_MINIMALIST_MODE)) {
      onUpgrade(PLANS.TINY.id, UPGRADE_MINIMALIST_PAYLOAD)
      return false
    }

    return true
  }

  // Handle access mode change with atomic update
  const handleAccessModeChange = (newMode) => {
    const updates = {
      gatedMode: newMode,
      enableEmailGate: newMode === GATED_MODE_EMAIL_GATE
    }

    // Handle password-related fields based on mode
    if (newMode !== GATED_MODE_PASSWORD) {
      updates.password = ''
      updates.passwordProtected = false
    } else {
      updates.passwordProtected = true
    }

    if (newMode !== GATED_MODE_EMAIL_WHITELIST) {
      updates.enableEmailWhitelist = false
      updates.allowedEmails = []
    } else {
      updates.enableEmailWhitelist = true
    }

    if (newMode !== GATED_MODE_SSO) {
      updates.enableSSO = false
    } else {
      updates.enableSSO = true
    }

    // Single atomic update - all related fields update together
    setFieldValue(name, {
      ...toggleValues,
      ...updates
    })
  }

  const handleDisplayModeChange = (newMode) => {
    setFieldValue(`${name}.displayMode`, newMode)
  }

  // Only render if there's a file selected and it's not a Wasmer file (PHP, Python, etc.)
  if ((!uploadValues.selectedFile && !site?.isTransferRequestLink) || isWasmerFile(uploadValues.selectedFile)) {
    return null
  }

  const onUpgrade = (planId, upgradePayload) => {
    dispatch(
      showUpgradeCardModal({
        ...upgradePayload,
        onClick: () => onUpgradePlan({ planId, userProfile }, dispatch)
      })
    )
  }

  return (
    <div id="access_settings" className={styles.section}>
      <span className={styles.sectionTitle}>
        {sectionIcon || <img src="/assets/icons/settings.svg" alt="settings" height="24" width="24" className="op-6" />}
        <h3 className="mb-0 font-18">Access Settings</h3>
      </span>

      {/* Access Mode Dropdown */}
      <div className={styles.accessOptionMainWrapper}>
        <div className={styles.accessOptionMain}>
          <div className={`${styles.optionIcon} ${styles.publicIcon}`}>
            <img src="/assets/icons/globe-pdf.svg" alt="access" height="24" width="24" className="op-6" />
          </div>
          <div className={styles.optionDetails}>
            <div className={styles.optionTitle}>Access Mode</div>
            <div className={styles.optionDescription}>Control how visitors can access your content</div>
          </div>
        </div>
        <div className={styles.optionToggle}>
          <AccessModeDropdown
            options={accessModeOptions}
            value={toggleValues.gatedMode}
            onChange={handleAccessModeChange}
            checkFeatureAccess={checkAccessAndDisplayModeFeature}
          />
        </div>
      </div>

      {/* Password Input - Only show when password protection is selected */}
      {toggleValues.gatedMode === GATED_MODE_PASSWORD && (
        <div className={styles.inputContainerStyle}>
          <div className={styles.formGroup}>
            <label htmlFor="passwordInput" className={styles.fieldLabel}>
              Password
            </label>
            <input
              id="passwordInput"
              className={styles.formControl}
              placeholder="Enter password"
              value={toggleValues.password}
              onChange={(e) => setFieldValue(`${name}.password`, e.target.value)}
              aria-label="Site Password"
            />
            <ErrorMessage name={`${name}.password`} component="div" className="validation-error text-center" />
          </div>
        </div>
      )}

      {toggleValues.gatedMode === GATED_MODE_EMAIL_WHITELIST && (
        <div className={styles.inputContainerStyle}>
          <div className={styles.formGroup}>
            <label htmlFor="emailWhitelistInput" className={styles.fieldLabel}>
              Emails to allow
            </label>
            <EmailTagInput
              id="emailWhitelistInput"
              emails={toggleValues.allowedEmails || []}
              onChange={(emails) => setFieldValue(`${name}.allowedEmails`, emails)}
            />
            <ErrorMessage name={`${name}.allowedEmails`} component="div" className="validation-error text-center" />
          </div>
        </div>
      )}

      {uploadValues?.fileTypes?.includes(PDF_FILE) &&
        (!uploadValues?.addedFiles || uploadValues?.addedFiles?.length === 0) &&
        uploadValues?.selectedFile?.size <= MAX_DIRECT_UPLOAD_SIZE && (
          <div className={styles.accessOptionMainWrapper}>
            <div className={styles.accessOptionMain}>
              <div className={`${styles.optionIcon} ${styles.publicIcon}`}>
                <img src="/assets/icons/presentation.svg" alt="access" height="24" width="24" className="op-6" />
              </div>
              <div className={styles.optionDetails}>
                <div className={styles.optionTitle}>Display Mode</div>
                <div className={styles.optionDescription}>Control how visitors can view your pdf</div>
              </div>
            </div>
            <div className={styles.optionToggle}>
              <AccessModeDropdown
                options={displayModeOptions}
                value={toggleValues.displayMode}
                onChange={handleDisplayModeChange}
                checkFeatureAccess={checkAccessAndDisplayModeFeature}
              />
            </div>
          </div>
        )}

      {/* Hide from Search Engines option */}
      <AccessOption
        iconClass="searchIcon"
        iconSrc="/assets/icons/eye-purple.svg"
        title="Hide from Search Engines"
        description="Prevent link from being indexed by Search Engines"
        control={
          <div className={styles.optionToggle}>
            <Toggle
              checked={toggleValues.disableIndexing}
              onChange={(e) => {
                // Check if user has access to this feature
                if (!productId) {
                  addUpgradeReason('Hide from Search Engines')
                  onUpgradePlan({ planId: PLANS.TINY.id, userProfile }, dispatch)
                  return
                }

                setFieldValue(`${name}.disableIndexing`, e.target.checked)
              }}
              id="hideFromSearchToggle"
              ariaLabel="Toggle hide from search engines"
              dataTestId="AccessOption-input-hide-search-engine"
            />
          </div>
        }
      />

      {/* Disable PDF Downloads - only for single PDF files */}
      {uploadValues?.fileTypes?.includes(PDF_FILE) &&
        (!uploadValues?.addedFiles || uploadValues?.addedFiles?.length === 0) && (
          <AccessOption
            iconClass="downloadsIcon"
            iconSrc="/assets/icons/download-pdf.svg"
            title="Disable Downloads"
            description="Prevent visitors from downloading your file"
            control={
              <div className={styles.optionToggle}>
                <Toggle
                  checked={toggleValues.disableExportPdf}
                  onChange={(e) => {
                    if (!productId) {
                      addUpgradeReason('Disable Downloads')
                      onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
                      return
                    }

                    setFieldValue(`${name}.disableExportPdf`, e.target.checked)
                  }}
                  id="disableDownloadsToggle"
                  ariaLabel="Toggle disable downloads"
                  dataTestId="AccessOption-input-disable-downloads"
                />
              </div>
            }
          />
        )}

      {/* Disable Collection Downloads - only for collections (multiple files) */}
      {(uploadValues?.fileTypes?.includes(ZIP_FILE) ||
        (uploadValues?.selectedFile && uploadValues?.addedFiles && uploadValues?.addedFiles?.length > 0)) && (
        <AccessOption
          iconClass="downloadsIcon"
          iconSrc="/assets/icons/download-pdf.svg"
          title="Disable Downloads"
          description="Prevent visitors from downloading any files"
          control={
            <div className={styles.optionToggle}>
              <Toggle
                checked={toggleValues.disableExportPdf}
                onChange={(e) => {
                  if (!productId) {
                    addUpgradeReason('Disable Downloads')
                    onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
                    return
                  }

                  setFieldValue(`${name}.disableExportPdf`, e.target.checked)
                }}
                id="disableCollectionDownloadsToggle"
                ariaLabel="Toggle disable collection downloads"
                dataTestId="AccessOption-input-disable-collection-downloads"
              />
            </div>
          }
        />
      )}

      {/* Error Configuration - only for ZIP files */}
      {uploadValues?.fileTypes?.length === 1 && uploadValues?.fileTypes?.includes(ZIP_FILE) && (
        <AccessOption
          iconClass="errorIcon"
          iconSrc="/assets/icons/error-pdf.svg"
          title="Error Configuration"
          description="Add a path to a custom 404 error page when files not found"
          control={
            <div className={styles.optionToggle}>
              <Toggle
                checked={toggleValues.siteConfiguration}
                onChange={(e) => {
                  // Check if user has access to this feature
                  if (!productId) {
                    addUpgradeReason('Error Configuration')
                    onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
                    return
                  }

                  const isChecked = e.target.checked
                  setFieldValue(`${name}.siteConfiguration`, isChecked)

                  // Set default error path when toggled on
                  if (isChecked) {
                    setFieldValue(`${name}.errorPath`, '/index.html')
                  } else {
                    setFieldValue(`${name}.errorPath`, '/')
                  }
                }}
                id="errorConfigToggle"
                ariaLabel="Toggle error configuration"
              />
            </div>
          }
        />
      )}

      {/* Error Path Input - only show when error configuration is enabled */}
      {uploadValues?.fileTypes?.length === 1 &&
        uploadValues?.fileTypes?.includes(ZIP_FILE) &&
        toggleValues?.siteConfiguration && (
          <div className={styles.inputContainerStyle}>
            <div className={styles.formGroup}>
              <label htmlFor="errorPathInput">Error Path</label>
              <input
                type="text"
                id="errorPathInput"
                className={styles.formControl}
                placeholder="/index.html"
                value={toggleValues.errorPath}
                onChange={(e) => setFieldValue(`${name}.errorPath`, e.target.value)}
                aria-label="Error Path"
              />
              <ErrorMessage name={`${name}.errorPath`} component="div" className="validation-error text-center" />
            </div>
          </div>
        )}
    </div>
  )
}

export default AccessSettings
